import React, { useEffect, useState } from 'react';
import { Loader2, Play, X, Video as VideoIcon } from 'lucide-react';
import { listInterviewClips, type LibraryClip } from '@/services/interviewApi';

// Video Library on the curriculum page — the interview clips recorded for this
// class. Read-only browse/preview; clips get into lessons from the lesson editor
// ("Choose from library"). Renders nothing until there's at least one clip.
export default function InterviewLibrarySection({ productId }: { productId?: string }) {
  const [clips, setClips] = useState<LibraryClip[]>([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<LibraryClip | null>(null);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    setLoading(true);
    listInterviewClips(productId)
      .then(c => { if (!cancelled) setClips(c); })
      .catch(() => { if (!cancelled) setClips([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [productId]);

  if (!productId || (!loading && clips.length === 0)) return null;

  // Group by guest, preserving order.
  const groups: { key: string; clips: LibraryClip[] }[] = [];
  const byKey = new Map<string, LibraryClip[]>();
  for (const c of clips) { const k = c.guestName || 'Guest'; if (!byKey.has(k)) { byKey.set(k, []); groups.push({ key: k, clips: byKey.get(k)! }); } byKey.get(k)!.push(c); }

  const src = (key: string) => key.startsWith('/api/') ? key : `/api/s3/${key}`;

  return (
    <div style={{ marginBottom: 20, padding: 16, borderRadius: 'var(--hj-r-md)', background: '#fff', border: '1px solid var(--hj-beige-line)' }}>
      <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
        <VideoIcon className="w-4 h-4" style={{ color: 'var(--hj-beige-ink-2)' }} />
        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>Video library</span>
        <span className="mono-label" style={{ marginLeft: 'auto', color: 'var(--hj-beige-ink-3)' }}>Interview clips · add to a lesson from its editor</span>
      </div>
      {loading ? (
        <div className="flex items-center justify-center" style={{ padding: 20 }}><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--hj-beige-ink-3)' }} /></div>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map(g => (
            <div key={g.key}>
              <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 8 }}>{g.key} · {g.clips.length} clip{g.clips.length === 1 ? '' : 's'}</div>
              <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
                {g.clips.map((clip, i) => (
                  <button key={clip.takeId} type="button" onClick={() => setPreview(clip)}
                    className="relative" style={{ display: 'block', padding: 0, border: '1px solid var(--hj-beige-line)', borderRadius: 8, overflow: 'hidden', background: '#000', aspectRatio: '16/9', cursor: 'pointer' }}>
                    {clip.thumbnailUrl ? <img src={src(clip.thumbnailUrl)} alt={`Take ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div className="w-full h-full" style={{ background: '#111' }} />}
                    <span className="absolute inset-0 flex items-center justify-center" style={{ background: 'oklch(0 0 0 / 0.2)' }}><Play className="w-5 h-5" style={{ color: '#fff', fill: '#fff', opacity: 0.9 }} /></span>
                    <span className="absolute" style={{ left: 5, bottom: 5, padding: '2px 6px', borderRadius: 6, background: 'oklch(0 0 0 / 0.6)', color: '#fff', fontSize: 10, fontWeight: 700 }}>Take {i + 1}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 70, background: 'oklch(0 0 0 / 0.72)', padding: 20 }} onClick={() => setPreview(null)}>
          <div style={{ width: '100%', maxWidth: 820, background: 'var(--hj-surface-1, #1a1a1a)', borderRadius: 'var(--hj-r-md)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2" style={{ padding: '10px 14px', background: '#000' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{preview.guestName}</span>
              <button type="button" onClick={() => setPreview(null)} className="inline-flex items-center justify-center" style={{ marginLeft: 'auto', width: 30, height: 30, borderRadius: 8, border: '1px solid oklch(1 0 0 / 0.2)', background: 'transparent', color: '#fff', cursor: 'pointer' }}><X className="w-4 h-4" /></button>
            </div>
            <video key={preview.takeId} src={src(preview.s3Key)} controls autoPlay poster={preview.thumbnailUrl ? src(preview.thumbnailUrl) : undefined} style={{ width: '100%', maxHeight: '70vh', display: 'block', background: '#000' }} />
          </div>
        </div>
      )}
    </div>
  );
}
